import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'; // Import FormsModule
import { ReactiveFormsModule } from '@angular/forms'; // Still import if you're using reactive forms too
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NewsitemService } from '../../../services/newsitem.service';
import { AuthService } from '../../../auth/auth.service';
import { firstValueFrom, Subject } from 'rxjs';
import { NewsItem } from '../../../interfaces/newsitem';
import { NgxEditorModule, Toolbar } from 'ngx-editor';
import { Editor } from 'ngx-editor';
import { toHTML } from 'ngx-editor';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-newsitem-add',
  imports: [
    CommonModule,
    FormsModule, // Add FormsModule here
    ReactiveFormsModule, // Optional if you are using reactive forms as well
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatDialogModule,
    MatDatepickerModule,
    MatIconModule,
    MatRadioModule,
    MatSnackBarModule,
    NgxEditorModule,
    MatMomentDateModule
  ],
  templateUrl: './newsitem-add.component.html',
  styleUrls: ['./newsitem-add.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class NewsitemAddComponent implements OnInit, OnDestroy {
  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private newsItemService: NewsitemService,
    private authService: AuthService,
    public dialogRef: MatDialogRef<NewsitemAddComponent>
  ) {}
  newsItemForm!: FormGroup;
  username: string = 'Unknown';
  responseSubjectNewNewsItem: Subject<NewsItem> = new Subject<NewsItem>();

  //Rich text format editor values
  editor!: Editor;
  toolbar: Toolbar = [
    // default value
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['undo', 'redo']
  ];
  ngOnInit(): void {
    this.editor = new Editor();
    this.getUserName();
    this.newsItemForm = this.fb.group(
      {
        title: [null, Validators.required],
        content: [null, Validators.required],
        expirationDate: [null],
        forInternal: [false],
        forCenter: [false],
        forLab: [false],
        userCreatedName: [this.username]
      },
      {
        validators: atLeastOneCheckboxValidator()
      }
    );
  }

  async getUserName() {
    const user = await firstValueFrom(this.authService.getUser());
    if (user) {
      this.username = user.username;
    }

    return this.username;
  }

  async submitNewsItem() {
    this.newsItemForm.value.userCreatedName = await this.getUserName();
    if (this.newsItemForm.invalid) {
      // Check if the error is due to the atLeastOne checkbox validation
      if (this.newsItemForm.errors?.['atLeastOne']) {
        this.showNotification('Please select at least one audience (Internal, Centers, or Laboratories).');
      } else {
        this.showNotification('Please fill out all required fields correctly.');
      }
      return;
    }

    let newsItemData = this.newsItemForm.value;
    this.newsItemForm.value.content = toHTML(this.newsItemForm.value.content);
    this.newsItemService.createNewsItem(newsItemData).subscribe({
      next: (response) => {
        console.log('News item  created:', response);
        this.showNotification('News item created successfully!');
        this.responseSubjectNewNewsItem.next(response);
        this.newsItemForm.reset({
          title: null,
          content: null,
          expirationDate: null,
          forInternal: false,
          forCenter: false,
          forLab: false,
          userCreatedName: this.username
        });
        this.dialogRef.close(); // Close the dialog and return updated item
      },
      error: (err) => {
        this.showNotification('Failed to create news item. Please try again.');
      }
    });
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }
}

export function atLeastOneCheckboxValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const forInternal = formGroup.get('forInternal')?.value;
    const forCenter = formGroup.get('forCenter')?.value;
    const forLab = formGroup.get('forLab')?.value;

    // If all checkboxes are false, return an error
    if (!forInternal && !forCenter && !forLab) {
      return { atLeastOne: true }; // Validation error
    }

    return null; // Validation passed
  };
}
